import { useState } from 'react'
import { CustomerAvatar } from '../components/CustomerAvatar'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { ChannelMark, Icon } from '../components/icons'
import type { ChannelType } from '@sac/contracts'

function money(n: number) {
  return `R$ ${n.toFixed(2).replace('.', ',')}`
}

function pct(part: number, total: number) {
  if (!total) return '0%'
  return `${Math.round((part / total) * 100)}%`
}

/**
 * Clientes — the CRM surface.
 *
 * Built around CPF coverage rather than a plain contact list, because CPF is what turns several
 * marketplace handles into one person, and the share of customers who have one is the honest
 * measure of how much of this data is actually usable for audiences.
 */
export function Clientes() {
  const [q, setQ] = useState('')
  const [hasCpf, setHasCpf] = useState<'' | 'yes' | 'no'>('')
  const [consent, setConsent] = useState<'' | 'yes' | 'no'>('')
  // Defaults to people who have written to us. Every marketplace buyer is now imported — that
  // completeness is what makes the conversion metric possible — but this screen sits next to the
  // inbox, and opening it on tens of thousands of strangers would bury the few hundred that the
  // team actually works with.
  const [contacted, setContacted] = useState<'' | 'yes' | 'no'>('yes')

  const { data: stats } = useQuery({ queryKey: ['crm', 'stats'], queryFn: () => api.crmStats() })
  const { data: rows, isLoading } = useQuery({
    queryKey: ['crm', 'customers', q, hasCpf, consent, contacted],
    queryFn: () =>
      api.crmCustomers({
        q,
        hasCpf: hasCpf || undefined,
        consent: consent || undefined,
        contacted: contacted || undefined,
      }),
  })

  const total = stats?.total ?? 0

  return (
    <div className="crm">
      <div className="crm-head">
        <h2>Clientes</h2>
        <p>
          Base unificada. O CPF é a chave que liga o mesmo comprador entre Shopee, Mercado Livre e
          WhatsApp — chega junto com a compra.
        </p>
      </div>

      <div className="crm-stats">
        <Stat label="Clientes" value={String(total)} />
        <Stat label="Com CPF" value={String(stats?.withCpf ?? 0)} sub={pct(stats?.withCpf ?? 0, total)} tone={(stats?.withCpf ?? 0) > 0 ? 'ok' : 'idle'} />
        <Stat label="Compraram" value={String(stats?.purchasers ?? 0)} sub={pct(stats?.purchasers ?? 0, total)} />
        <Stat label="E-mail" value={String(stats?.withEmail ?? 0)} sub={pct(stats?.withEmail ?? 0, total)} />
        <Stat label="Telefone" value={String(stats?.withPhone ?? 0)} sub={pct(stats?.withPhone ?? 0, total)} />
        <Stat
          label="Consentimento"
          value={String(stats?.consented ?? 0)}
          sub="públicos Meta"
          tone={(stats?.consented ?? 0) > 0 ? 'ok' : 'warn'}
        />
      </div>

      <div className="crm-filters">
        <select value={contacted} onChange={(e) => setContacted(e.target.value as '' | 'yes' | 'no')}>
          <option value="yes">Falaram com o SAC</option>
          <option value="no">Compraram sem contato</option>
          <option value="">Todos</option>
        </select>
        <div className="psearch" style={{ flex: 1 }}>
          <Icon name="search" />
          <input
            placeholder="Buscar por nome, CPF, e-mail, telefone ou usuário do canal…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <Pills
          value={hasCpf}
          onChange={setHasCpf}
          options={[['', 'CPF: todos'], ['yes', 'Com CPF'], ['no', 'Sem CPF']]}
        />
        <Pills
          value={consent}
          onChange={setConsent}
          options={[['', 'Consent.: todos'], ['yes', 'Com'], ['no', 'Sem']]}
        />
      </div>

      {isLoading ? (
        <div className="empty" style={{ padding: 28 }}>Carregando…</div>
      ) : !rows?.length ? (
        <div className="empty" style={{ padding: 28 }}>Nenhum cliente encontrado.</div>
      ) : (
        <div className="crm-table">
          <div className="crm-row crm-th">
            <span>Cliente</span>
            <span>Canais</span>
            <span>CPF</span>
            <span>Pedidos</span>
            <span>Total</span>
            <span>Consent.</span>
          </div>
          {rows.map((c) => (
            <div className="crm-row" key={c.id}>
              <span className="crm-who">
                <CustomerAvatar seed={c.id} color={c.avatarColor} name={c.displayName} size={30} />
                <span>
                  <span className="t1">{c.fullName ?? c.displayName}</span>
                  <span className="t2">{c.email ?? c.phone ?? c.city ?? '—'}</span>
                </span>
              </span>
              <span className="crm-chans">
                {c.channels.length
                  ? c.channels.map((ch) => <ChannelMark key={ch} channel={ch as ChannelType} size={15} />)
                  : <span className="t2">—</span>}
              </span>
              <span>
                {c.hasCpf ? (
                  // Masked on purpose: enough to confirm against what a customer reads out,
                  // not enough to copy. The full value is a separate, audited request.
                  <code className="crm-cpf">{c.cpf}</code>
                ) : (
                  <span className="t2">—</span>
                )}
              </span>
              <span>{c.orderCount || <span className="t2">0</span>}</span>
              <span>{c.totalSpent ? money(c.totalSpent) : <span className="t2">—</span>}</span>
              <span>
                {c.suppressedAt ? (
                  <span className="tag no">Suprimido</span>
                ) : c.marketingConsent ? (
                  <span className="tag ok">Sim</span>
                ) : (
                  <span className="tag">Não</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'ok' | 'warn' | 'idle' }) {
  return (
    <div className={`crm-stat ${tone ?? ''}`}>
      <span className="v">{value}</span>
      <span className="l">{label}</span>
      {sub && <span className="s">{sub}</span>}
    </div>
  )
}

function Pills<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T
  onChange: (v: T) => void
  options: [T, string][]
}) {
  return (
    <div className="crm-pills">
      {options.map(([v, label]) => (
        <button key={v} className={`fpill ${value === v ? 'on' : ''}`} onClick={() => onChange(v)}>
          {label}
        </button>
      ))}
    </div>
  )
}
